import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
} from '@mui/material';
import { Email as EmailIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useForgotPasswordMutation } from '../../store/api/authApi';
import { palette } from '../../theme/palette';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../store/hooks';

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { language } = useAppSelector((state) => state.ui);
  const [forgotPassword, { isLoading }] = useForgotPasswordMutation();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!email.trim()) {
      setError(t('auth.forgotPassword.emailRequired'));
      return;
    }

    try {
      await forgotPassword({ email: email.trim().toLowerCase() }).unwrap();
      setSuccess(t('auth.forgotPassword.otpSent'));
      // Navigate to verify OTP page after short delay
      setTimeout(() => {
        navigate('/verify-reset-otp', { state: { email: email.trim().toLowerCase() } });
      }, 1500);
    } catch (err: unknown) {
      const error = err as { data?: { message?: string } };
      setError(error?.data?.message || t('auth.forgotPassword.error'));
    }
  };

  return (
    <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
      <Card
        elevation={8}
        sx={{
          width: '100%',
          borderRadius: { xs: 2, sm: 3 },
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h4"
              fontWeight={700}
              sx={{
                background: `linear-gradient(180deg, ${palette.brand[500]} 0%, ${palette.brandSecondary[500]} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {t('auth.forgotPassword.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('auth.forgotPassword.subtitle')}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              name="email"
              label={t('auth.forgotPassword.emailLabel')}
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setError(null); }}
              required
              placeholder={language === 'ar' ? 'أدخل بريدك الإلكتروني' : 'Enter your email'}
              dir={language === 'ar' ? 'rtl' : 'ltr'}
              sx={{
                mb: 3,
                '& .MuiInputBase-input': {
                  textAlign: language === 'ar' ? 'right' : 'left',
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isLoading}
              sx={{
                py: 1.5,
                mb: 2,
                background: `linear-gradient(180deg, ${palette.brand[500]} 0%, ${palette.brandSecondary[500]} 100%)`,
                '&:hover': {
                  background: `linear-gradient(180deg, ${palette.brand[600]} 0%, ${palette.brandSecondary[600]} 100%)`,
                },
              }}
            >
              {isLoading ? t('auth.forgotPassword.sending') : t('auth.forgotPassword.sendOTP')}
            </Button>

            <Button
              fullWidth
              variant="text"
              startIcon={<ArrowBackIcon sx={{ transform: language === 'ar' ? 'rotate(180deg)' : 'none' }} />}
              onClick={() => navigate('/login')}
              sx={{ color: palette.brandSecondary[500] }}
            >
              {t('auth.forgotPassword.backToLogin')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ForgotPasswordPage;

